// FEATURE-88 — read + validate the host-provided `clientCode`.
//
// Two host-facing sources collapse to a single `string`:
//   1. `BAAssistant.init({ clientCode })` — preferred. Validated here.
//   2. `<meta name="ba-assistant-client" content="...">` — legacy embed
//      fallback for hosts that can't run JS at init time.
//
// Resolution is "explicit init wins over meta tag". Format is the kebab-case
// pattern shared with the BE DTO (CLIENT_CODE_PATTERN).
//
// Phase 5 (current): `clientCode` is REQUIRED. Missing or malformed values
// THROW a descriptive `Error` from `BAAssistant.init(...)` so the host
// integrator sees the failure immediately in the browser console. The BE
// equally rejects requests without `clientCode` (`400 CLIENT_CODE_REQUIRED`),
// so silently dropping it would only produce a less helpful failure mode.

import { CLIENT_CODE_PATTERN } from '@ba/protocol';

const META_NAME = 'ba-assistant-client';

/** Returns the raw `<meta name="ba-assistant-client">` content (untrimmed). */
export function readMetaClientCode(doc: Document = document): string | undefined {
  const meta = doc.querySelector(`meta[name="${META_NAME}"]`);
  if (meta === null) return undefined;
  const raw = meta.getAttribute('content');
  return raw === null || raw.length === 0 ? undefined : raw;
}

/**
 * Resolve a single `clientCode` from the two host sources and validate format.
 *
 * Phase 5 — clientCode is required. Missing or malformed values throw a
 * descriptive `Error` that surfaces in the host devtools so the integrator
 * fixes the embed immediately. There is no silent fallback anymore.
 */
export function resolveClientCode(
  explicit: string | undefined,
  fallback: string | undefined,
): string {
  const raw = (explicit ?? fallback)?.trim();
  if (raw === undefined || raw.length === 0) {
    throw new Error(
      'BAAssistant: clientCode is required (got: undefined).\n' +
        "Set BAAssistant.init({ clientCode: 'your-tenant-code', ... }) or add\n" +
        '<meta name="ba-assistant-client" content="your-tenant-code">.',
    );
  }
  if (!CLIENT_CODE_PATTERN.test(raw)) {
    throw new Error(
      `BAAssistant: clientCode "${raw}" is malformed (got: ${JSON.stringify(raw)}).\n` +
        'Expected ^[a-z0-9-]{3,64}$ (kebab-case).\n' +
        "Set BAAssistant.init({ clientCode: 'your-tenant-code', ... }) or add\n" +
        '<meta name="ba-assistant-client" content="your-tenant-code">.',
    );
  }
  return raw;
}
